import { html, type PropertyDeclarations } from "lit";
import { LmaxBaseChartComponent } from "./lmax-base-chart-component";
import { getScriptPath } from "../utils/common";

/**
 * Shared panel-chrome wrapper every chart nests inside — see
 * `LmaxBaseChartComponent`'s doc comment for the general pattern. Renders
 * only `.chart-panel` (background/border/radius/padding) around a
 * `<slot>`; doesn't know or care what's nested inside it.
 *
 * No `static styles` here — like `<lmax-web-latency-chart>` and
 * `<lmax-web-spread-chart>`, its shadow-DOM CSS is real CSS in
 * `public/assets/styles/chart-panel.css`, loaded via a `<link>` it renders
 * itself (see `defaultStyleHref`, overridable via the `stylehref`
 * attribute).
 */
export class LmaxChartComponent extends LmaxBaseChartComponent {
  static properties: PropertyDeclarations = {
    styleHref: { type: String, attribute: "stylehref" },
  };

  declare styleHref: string;

  constructor() {
    super();
    this.styleHref = "";
  }

  /**
   * Default URL for this component's shadow-DOM stylesheet, used unless
   * the `stylehref` attribute supplies one instead. In dev, the file's own
   * root-relative dev-server path (`vite.config.ts` sets `root: "public"`,
   * so Vite serves it there for free). In production, `getScriptPath()`
   * resolves the URL relative to *this deployment's own base path*
   * instead, backed by `vite.config.ts`'s `copyPublicDirsToDist` plugin,
   * which copies this file into the build output at that same path
   * relative to the JS chunk, unhashed.
   */
  private get defaultStyleHref(): string {
    return import.meta.env.DEV
      ? "/assets/styles/chart-panel.css"
      : `${getScriptPath()}styles/chart-panel.css`;
  }

  render() {
    return html`
      <link rel="stylesheet" href=${this.styleHref || this.defaultStyleHref} />
      <div class="chart-panel"><slot></slot></div>
    `;
  }
}

customElements.define("lmax-chart-component", LmaxChartComponent);

declare global {
  interface HTMLElementTagNameMap {
    "lmax-chart-component": LmaxChartComponent;
  }
}
